import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const slides = [
    {
      image: 'https://public.readdy.ai/ai/img_res/2e8380239fb84989bf687018bd0a9f2b.jpg',
      headlineKey: 'hero.slide1.headline',
      subKey: 'hero.slide1.sub',
      taglineKey: 'hero.slide1.tagline',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Japan%20cherry%20blossom%20sakura%20pathway%20with%20lanterns%20and%20traditional%20torii%20gate%20arch%2C%20pink%20flowers%2C%20misty%20mountain%20background%2C%20magical%20spring%20scenery%2C%20travel%20photography%20Japan&width=1920&height=900&seq=1001b&orientation=landscape',
      headlineKey: 'hero.slide2.headline',
      subKey: 'hero.slide2.sub',
      taglineKey: 'hero.slide2.tagline',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=European%20Alps%20mountain%20panorama%20with%20green%20meadows%20wildflowers%20small%20village%20church%20and%20snow-capped%20peaks%2C%20idyllic%20Swiss%20scenery%2C%20clear%20blue%20sky%2C%20stunning%20landscape%20travel%20photography&width=1920&height=900&seq=1001c&orientation=landscape',
      headlineKey: 'hero.slide3.headline',
      subKey: 'hero.slide3.sub',
      taglineKey: 'hero.slide3.tagline',
    },
  ];

  const goTo = (index: number) => {
    if (animating) return;
    setAnimating(true);
    setCurrent(index);
    setTimeout(() => setAnimating(false), 800);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [slides.length]);

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === current ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="w-full h-full">
            <img
              src={slide.image}
              alt={t(slide.headlineKey)}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/55"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 w-full h-full flex items-center justify-center">
        <div className="w-full text-center px-6 max-w-4xl mx-auto">
          <div
            key={current}
            style={{ animation: 'fadeSlideUp 0.9s ease forwards' }}
          >
            <p className="text-amber-400 font-medium tracking-widest text-sm uppercase mb-4">
              <i className="ri-map-pin-2-line mr-2"></i>{t('hero.badge')}
            </p>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-3 font-playfair leading-tight">
              {t(slides[current].headlineKey)}
            </h1>
            <h2 className="text-4xl md:text-6xl font-bold text-amber-400 mb-6 font-playfair leading-tight">
              {t(slides[current].subKey)}
            </h2>
            <p className="text-white/85 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              {t(slides[current].taglineKey)}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap">
              <Link
                to="/tours"
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-4 rounded-md transition-all duration-200 text-base whitespace-nowrap cursor-pointer"
              >
                <i className="ri-compass-line mr-2"></i>{t('hero.cta.explore')}
              </Link>
              <a
                href="https://www.easybook.com/la-la/bus/operator"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-md transition-all duration-200 text-base whitespace-nowrap cursor-pointer"
              >
                <i className="ri-bus-2-line mr-2"></i>{t('hero.cta.bus')}
              </a>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-emerald-800 font-semibold px-8 py-4 rounded-md transition-all duration-200 text-base whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>{t('hero.cta.contact')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-8 left-0 right-0 z-10 flex justify-center gap-3">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current ? 'bg-amber-400 w-8 h-2' : 'bg-white/50 w-2 h-2 hover:bg-white/80'
            }`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll Hint */}
      <div className="absolute bottom-8 right-8 z-10 text-white/60 flex flex-col items-center gap-2 text-xs">
        <i className="ri-mouse-line text-lg"></i>
        <span>Scroll</span>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
